import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import env from '../utils/env';

interface FilterOptions {
    experimentIds?: string[];
    providers?: string[];
    models?: string[];
    types?: string[];
    experimentTypes?: string[];
    tags?: string[];
    categories?: string[];
}

interface UseFilterOptionsResult {
    filterOptions: FilterOptions;
    loading: boolean;
    error: string | null;
}

// Hook for experiments filter options
export const useExperimentsFilterOptions = (): UseFilterOptionsResult => {
    const { token } = useAuth();
    const [filterOptions, setFilterOptions] = useState<FilterOptions>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const apiBaseUrl = env.apiUrl;

    useEffect(() => {
        const fetchFilterOptions = async () => {
            if (!token) {
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                const response = await fetch(`${apiBaseUrl}/admin/experiments/filter-options`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (response.ok) {
                    const data = await response.json();
                    setFilterOptions({
                        experimentIds: data.experimentIds || [],
                        providers: data.providers || [],
                        models: data.models || [],
                        types: data.types || [],
                        tags: data.tags || []
                    });
                } else {
                    throw new Error(`Failed to fetch filter options: ${response.statusText}`);
                }
            } catch (err: any) {
                setError(err.message);
                console.error('Failed to fetch experiments filter options:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchFilterOptions();
    }, [token, apiBaseUrl]);

    return { filterOptions, loading, error };
};

// Hook for report experiments filter options
export const useReportExperimentsFilterOptions = (): UseFilterOptionsResult => {
    const { token } = useAuth();
    const [filterOptions, setFilterOptions] = useState<FilterOptions>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const apiBaseUrl = env.apiUrl;

    useEffect(() => {
        const fetchFilterOptions = async () => {
            if (!token) {
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                const response = await fetch(`${apiBaseUrl}/admin/report-experiments/filter-options`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (response.ok) {
                    const data = await response.json();
                    setFilterOptions({
                        experimentIds: data.experimentIds || [],
                        providers: data.providers || [],
                        models: data.models || [],
                        experimentTypes: data.experimentTypes || [],
                        tags: data.tags || [],
                        categories: data.categories || []
                    });
                } else {
                    throw new Error(`Failed to fetch filter options: ${response.statusText}`);
                }
            } catch (err: any) {
                setError(err.message);
                console.error('Failed to fetch report experiments filter options:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchFilterOptions();
    }, [token, apiBaseUrl]);

    return { filterOptions, loading, error };
}; 